import React, {useEffect, useRef} from "react";
import PageFooter from "../PageFooter";
import {useNavigate, useParams} from "react-router-dom";
import {useTheCellMapContext} from "../../contexts/TheCellMapContext";
import useGeneByIdentifier from "../../hooks/useGeneByIdentifier";
import {useGeneRefs} from "../../hooks/useGeneRefs";
import {useLibraryData} from "../../hooks/useLibraryData";
import {usePlotly} from "../../hooks/usePlotly";
import {FigureType} from "../../types/enums";
import {ListScoreFigureData} from "../../types/list";
import {SearchOption} from "../../types/Types";
import SearchBox from "../SearchBox";
import "../../css/gene.css";
import GeneExpressionFitness from "./GeneExpressionFitness";
import GeneFigure from "./GeneFigure";
import GeneHeader from "./GeneHeader";
import GeneQueryFigures from "./GeneQueryFigures";

const GeneUserInterface = () => {
  const {setSelectedGenes, selectedScreen, setSelectedScreen} = useTheCellMapContext();

  const navigate = useNavigate();
  const {identifier} = useParams<{ identifier: string }>();
  const {gene, isLoaded, isEntrezId, isAlias} = useGeneByIdentifier(identifier);

  const geneRefs = useGeneRefs();
  const plotlyLoaded = usePlotly();
  const libraryData = useLibraryData(gene);
  const pageRef = useRef<HTMLDivElement>(null);

  const switchScreen = (ginId: string) => {
    const page = pageRef.current;
    if (!page) {
      setSelectedScreen(ginId);
      return;
    }
    const scrollTop = page.scrollTop;
    setSelectedScreen(ginId);
    const lock = () => {
      page.scrollTop = scrollTop;
    };
    page.addEventListener('scroll', lock);
    setTimeout(() => page.removeEventListener('scroll', lock), 500);
  };

  useEffect(() => {
    const ids = gene?.q && gene?.g?.length ? gene.g.map(g => g.split(/\s+/)[1]).filter(Boolean) : [];
    if (!ids.includes(selectedScreen ?? '')) setSelectedScreen(ids[0] ?? null);
  }, [gene?.e]);

  useEffect(() => {
    if (!isLoaded || !gene || (!isEntrezId && !isAlias)) return;
    navigate(`/gene/${gene.s}`, {replace: true});
  }, [isLoaded, gene, isEntrezId, isAlias, navigate]);

  const handleGeneSelected = (option: SearchOption) => {
    setSelectedGenes([]);
    navigate(`/gene/${option.symbol}`);
  };

  if ((isEntrezId || isAlias) && gene) return null;

  if (!isLoaded) return (
    <div id="gene-page" className="bg-white overflow-auto flex-fill">
      <div className="container py-4"><p>Loading...</p></div>
    </div>
  );

  if (!gene) return (
    <div id="gene-page" className="bg-white overflow-auto flex-fill">
      <div className="container py-4">
        <h4>Gene not found: <code>{identifier}</code></h4>
        <p className="text-muted">No gene matching this symbol or Entrez ID was found in the dataset.</p>
        <div className="gene-search mt-3">
          <SearchBox isMulti={false} lastAdded={handleGeneSelected} mobileInline={true}/>
        </div>
      </div>
    </div>
  );

  const refs = geneRefs?.[gene.e];
  const gParam = encodeURIComponent(`${gene.s}::${gene.e}`);
  const ginIds = !!gene.q && gene.g?.length ? gene.g.map(g => g.split(/\s+/)[1]).filter(Boolean) : [];
  const showQueryFigures = ginIds.length > 0 && plotlyLoaded && !!selectedScreen && ginIds.includes(selectedScreen);
  const figData: ListScoreFigureData = {query: gene.e, screen_id: selectedScreen ?? '', window: 'preview', height: 500};

  return (
    <div id="gene-page" className="bg-white overflow-auto flex-fill" ref={pageRef}>
      <div className="container py-4">

        <GeneHeader gene={gene} gParam={gParam} refs={refs} onGeneSelected={handleGeneSelected}/>

        {libraryData && <>
            <hr className="my-4 opacity-25"/>
            <GeneExpressionFitness libraryData={libraryData}/>
        </>}

        {showQueryFigures && <>
            <hr className="my-4 opacity-25"/>
            <GeneQueryFigures
                ginIds={ginIds}
                selectedScreen={selectedScreen!}
                figData={figData}
                onSwitchScreen={switchScreen}
            />
        </>}

        {!!gene.l && plotlyLoaded && <>
            <hr className="my-4 opacity-25"/>
            <h6 className="section-label border-success">Library Scores</h6>
            <div className="row justify-content-center">
                <div className="col-12 col-xl-6">
                    <GeneFigure type={FigureType.ScoreLibrary} data={{library: gene.b, window: 'preview'}}
                                labelClass="border-success"/>
                </div>
            </div>
        </>}

      </div>
      <PageFooter/>
    </div>
  );
};

export default GeneUserInterface;